import {Injectable} from '@angular/core';
import {QueryParamsHandling, Router} from '@angular/router';
import {EMPTY, finalize, Observable, Subscription, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {ResetPaymentMethodStore} from '../../../common/ngrx-store/action/payment-method.action';
import {ResetButtonSettings} from '../../../common/ngrx-store/action/button-settings.action';
import {ResetGlobalSettingsServices} from '../../../common/ngrx-store/action/user-global-services.action';
import {ResetSettingsServices} from '../../../common/ngrx-store/action/user-services.action';
import {AccountService, InfoWidgetResponse} from '../../../services/account.service';
import {ResetBreadcrumb} from '../../../common/ngrx-store/action/breadcrumb.action';
import {AuthApiService} from './auth-api.service';
import {AppState} from '../../../common/ngrx-store/state/app.state';
import {AuthQuery} from '../../../common/elf-store/auth/auth.query';
import {AuthManager} from '../../../common/elf-store/auth/auth.manager';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe.decorator';
import {UserInfoManager} from '../../../common/elf-store/user-info/user-info.manager';
import {UserInfoQuery} from '../../../common/elf-store/user-info/user-info.query';
import {UserInfoState} from '../../../common/elf-store/user-info/user-info.type';
import {LocalStorage} from 'src/app/decorators/local-storage.decorator';
import {SitesSearchParamsManager} from '../../../common/elf-store/sites-search-params/sites-search-params.manager';
import {MySitesService} from '../../../services/my-sites.service';
import {ToastrMessagesProvider} from 'src/app/services/toastr-messages.service';
import {TeamManagementApiService} from 'src/app/services/api/team-management-api.service';
import {UserApiService} from '../../../services/api/users-api.service';
import {AgencyService} from 'src/app/services/agency.service';
import {NotificationsService} from 'src/app/services/notifications.service';
import {TrackingService} from 'src/app/services/tracking.service';
import {GetAccountByEmailResponseDto, UserRegistrationBodyDto} from '../../../../userway/sdk/UserWayApiClient';
import {
  PasswordResetEmailRequestDto,
  ResetPasswordDto,
  SsoErrorResponseDto,
  UserLoginDto,
} from '../../../../userway/sdk/SSOApiClient';
import {EventEmitterService} from '../../../services/event-emitter.service';
import {AmplitudeService} from '../../../services/amplitude.service';
import {ApiClient} from '../../../services/api/userway-api.service';

@Injectable({
  providedIn: 'root',
})
@AutoUnsubscribe()
export class AuthService {
  authUserEmail = '';
  existEmail: string;
  isLoginAs: boolean;
  isSSOLogin: boolean;
  isMfa: boolean;
  @LocalStorage('') uWTargetSwitchId: string;
  teamAccountCode: string = null;
  /* eslint-disable local/no-subscription-variables */
  tokenSubscription: Subscription;
  refreshTokenSub: Subscription;
  userInfoSub: Subscription;
  /* eslint-enable local/no-subscription-variables */
  infoAccountCode: string;
  isRegistrationPage: boolean = false;

  isAgency: boolean;
  isFixMyCodeEnabled: boolean;

  // TODO: change private with get and remove related methods
  private token: string = null;
  private refreshToken: string = null;
  private refreshTokenPending: boolean;
  private regulationAllowed: boolean = false;

  constructor(
    private router: Router,
    private authQuery: AuthQuery,
    private authManager: AuthManager,
    private api: AuthApiService,
    private store: Store<AppState>,
    private accountService: AccountService,
    private userInfoManager: UserInfoManager,
    private eventEmitterService: EventEmitterService,
    private userInfoQuery: UserInfoQuery,
    private sitesSearchParamsManager: SitesSearchParamsManager,
    private toastr: ToastrMessagesProvider,
    private mySitesService: MySitesService,
    private teamManageApiService: TeamManagementApiService,
    private userApiService: UserApiService,
    private agencyService: AgencyService,
    private notificationService: NotificationsService,
    private trackingService: TrackingService,
    private amplitudeService: AmplitudeService,
    private apiClient: ApiClient,
  ) {
    this.subscribeTokens();
    this.initRefreshErrorHandler();
  }

  get isUserSwitchedToTeam(): boolean {
    return !!this.uWTargetSwitchId;
  }

  subscribeTokens() {
    this.tokenSubscription = this.authQuery.token$.subscribe(token => (this.token = token));
    this.refreshTokenSub = this.authQuery.refreshToken$.subscribe(refreshToken => (this.refreshToken = refreshToken));

    this.userInfoSub = this.userInfoQuery.userInfo$.subscribe((user: UserInfoState) => {
      this.isAgency = user.agencyAccount?.enabled;
      this.regulationAllowed = user.open_regulation_tracker;
      this.infoAccountCode = user.accountCode;
      this.isFixMyCodeEnabled = user.isFixMyCodeEnabled;
    });
  }

  isUserAuthenticated(): boolean {
    return !!this.token;
  }

  isRegulationAllowed(): boolean {
    return this.regulationAllowed;
  }

  sendUserLoginForm(user: UserLoginDto): Observable<any> {
    return this.api.signIn(user).pipe(
      tap(response => {
        this.setTokenToApplication(response.payload.accessToken);
        this.setRefreshTokenToApplication(response.payload.refreshToken);
      }),
    );
  }

  sendUserRegistrationForm(signUp: UserRegistrationBodyDto): Observable<any> {
    return this.userApiService.signUp(signUp).pipe(
      tap(response => {
        this.setTokenToApplication(response.data.accessToken);
        this.setRefreshTokenToApplication(response.data.refreshToken);
      }),
    );
  }

  emailIsExists(email: string): Observable<GetAccountByEmailResponseDto> {
    return this.userApiService.checkEmail(email);
  }

  sendUserForgotPasswordForm(forgot: PasswordResetEmailRequestDto): Observable<any> {
    return this.api.forgot(forgot);
  }

  sendEmailVerification(email: string): Observable<any> {
    return this.api.sendEmailVerification(email).pipe(map(response => response.payload));
  }

  sendUserRecoveryForm(recovery: ResetPasswordDto): Observable<any> {
    return this.api.recovery(recovery).pipe(
      tap(response => {
        const token = response.payload.accessToken;
        localStorage.setItem('UserWayManageAppToken', token);
        this.setToken(token);
      }),
    );
  }

  setToken(token: string): void {
    this.authManager.setToken(token);
    this.parseToken(this.token);
  }

  parseToken(token: string): any {
    if (!token) {
      return;
    }
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c: string) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
      const data = JSON.parse(jsonPayload);
      data.sub = JSON.parse(data.sub);
      this.isLoginAs = data?.sub?.loginType === 'ADMIN_LOGIN_AS';
      this.isSSOLogin = data?.sub?.loginMechanism === 'SSO_LOGIN';
      this.isMfa = data?.sub?.enabledMfa;
    } catch (e) {
      console.error(`auth-service - parseToken - ${e.message}`);
    }
  }

  userLogOut(isUserLogout: boolean): void {
    const accountCode = JSON.parse(JSON.stringify({userId: this.infoAccountCode}));
    const siteTableColumns = localStorage.getItem(`${this.infoAccountCode}_displayedColumns`);
    this.setToken(null);
    this.authManager.reset();
    localStorage.removeItem('UserWayManageAppToken');
    // TODO Think about using akita store instead of Ngrx
    this.store.dispatch(new ResetButtonSettings());
    this.store.dispatch(new ResetPaymentMethodStore());
    this.store.dispatch(new ResetGlobalSettingsServices());
    this.store.dispatch(new ResetSettingsServices());
    this.userInfoManager.resetAndLogout();
    this.store.dispatch(new ResetBreadcrumb());
    this.accountService.newUserSiteId = null;
    this.accountService.shouldShowWelcomePopup = true;
    this.clearTargetId();
    const localStorageData = localStorage.getItem('is_feedback_submitted');
    localStorage.setItem('sitesPerPage', String(10));
    this.sitesSearchParamsManager.resetAll();
    this.mySitesService.isSitesDataFetched = false;
    this.agencyService.clearAgencyData();
    this.notificationService.resetNotifications();
    this.trackingService.resetUserTokenData();
    this.amplitudeService.resetIdentity();
    this.apiClient.clearHeaders();
    if (isUserLogout) {
      localStorage.clear();
      this.router.navigate(['/login']);
      if (localStorageData) {
        localStorage.setItem('is_feedback_submitted', localStorageData);
      }
      if (siteTableColumns) {
        localStorage.setItem(`${accountCode.userId}_displayedColumns`, siteTableColumns);
      }
    }
  }

  setTokenToApplication(token: string): void {
    localStorage.setItem('UserWayManageAppToken', token);
    this.setToken(token);
  }

  setRefreshTokenToApplication(refreshToken: string): void {
    localStorage.setItem('UserWayManageAppJwtRefreshToken', refreshToken);
    this.authManager.setRefreshToken(refreshToken);
  }

  getRefreshTokenPending(): boolean {
    return this.refreshTokenPending;
  }

  setRefreshTokenPending(pending: boolean): void {
    this.refreshTokenPending = pending;
  }

  updateRefreshToken(): Observable<any> {
    this.setRefreshTokenPending(true);
    return this.api.refreshToken(this.refreshToken).pipe(
      tap(response => {
        this.setTokenToApplication(response.payload.accessToken);
        this.setRefreshTokenToApplication(response.payload.refreshToken);
      }),
      map(response => response.payload),
    );
  }

  getServiceAccount(domain: string): Observable<any> {
    return this.api.getServiceAccount(domain).pipe(map(response => response.payload));
  }

  setTargetTeamId(id: string) {
    this.uWTargetSwitchId = id;
  }

  clearTargetId() {
    localStorage.removeItem('uWTargetSwitchId');
    this.teamAccountCode = null;
  }

  initiateGoogleOrSSOAuth(url: string) {
    window.open(url, '_blank', 'height=700,width=700,status=yes,toolbar=no,menubar=no,location=no');
    window.addEventListener('message', (event: any) => {
      if (event.data && event.data.SOCIAL_LOGIN_SUCCESS) {
        const {authCode} = event.data.SOCIAL_LOGIN_SUCCESS;
        this.setCode(authCode, undefined, 'preserve');
      }
      if (event.data && event.data.SOCIAL_LOGIN_FAILURE) {
        this.toastr.warning('Can not be accessed by this account');
      }
    });
  }

  handleTokenResponse(res: any, redirect: string | undefined, queryParamsHandling: QueryParamsHandling = ''): void {
    this.setToken(res.accessToken);
    this.authManager.setRefreshToken(res.refreshToken);
    this.clearTargetId();
    localStorage.setItem('UserWayManageAppToken', res.accessToken);
    localStorage.setItem('UserWayManageAppJwtRefreshToken', res.refreshToken);

    const fallbackRoute = () => this.router.navigate(['/'], {queryParamsHandling});

    if (!redirect) {
      fallbackRoute();
      return;
    }
    try {
      const url = new URL(decodeURIComponent(redirect), window.location.origin);
      this.router.navigate([url.pathname], {
        queryParams: Object.fromEntries(url.searchParams),
        queryParamsHandling,
        fragment: url.hash.substring(1) || null,
      });
    } catch (e) {
      console.error(e);
      fallbackRoute();
    }
  }

  setCode(code: string, redirect?: string, queryParamsHandling?: QueryParamsHandling): void {
    this.accountService.oauthExchange(code).subscribe(res => {
      this.handleTokenResponse(res, redirect, queryParamsHandling);
    });
  }

  loadUserOrTeamDataBasedOnSwitch(): Observable<InfoWidgetResponse> {
    return this.isUserSwitchedToTeam
      ? this.teamManageApiService.getTeamsMembership().pipe(
          switchMap(membership => {
            const currentTeam = membership.find(item => item.myMembershipTeamOwnerAccountId === this.uWTargetSwitchId);
            if (!currentTeam) {
              this.clearTargetId();
              location.reload();
              return EMPTY;
            }
            this.teamAccountCode = currentTeam?.myMembershipTeamOwnerInfo?.accountCode;
            return this.accountService.getTeamOwnerInfo(this.teamAccountCode);
          }),
        )
      : this.accountService.getUserInfo();
  }

  initRefreshErrorHandler() {
    this.eventEmitterService.handleExpiredToken.subscribe(logout => {
      if (logout) {
        return this.userLogOut(true);
      }
      this.refreshTokens().subscribe();
    });
  }

  refreshTokens(): Observable<void> {
    if (this.getRefreshTokenPending()) {
      return EMPTY;
    }
    this.setRefreshTokenPending(true);
    return this.updateRefreshToken().pipe(
      switchMap(res => this.handleTokenRefreshResponse(res)),
      catchError((err: SsoErrorResponseDto) => this.handleTokenError(err)),
      finalize(() => this.setRefreshTokenPending(false)),
    );
  }

  private handleTokenRefreshResponse(res: {refreshToken: string; accessToken: string}): Observable<void> {
    if (!res.refreshToken || !res.accessToken) {
      this.userLogOut(true);
      return throwError(() => new Error('Token refresh failed'));
    }
    location.reload();
    return EMPTY;
  }

  private handleTokenError(err: SsoErrorResponseDto): Observable<never> {
    this.userLogOut(true);
    return throwError(() => err);
  }
}
